import { motion } from "framer-motion";
import { MessageCircleQuestion } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const FAQS = [
  {
    q: "Is Cyrion really non-custodial?",
    a: "Yes. Your private keys are generated and sealed inside the cold-storage engine on your own machine. They are never transmitted, never backed up to our servers, and never visible to us. If Cyrion disappeared tomorrow, your recovery phrase still restores everything.",
  },
  {
    q: "How do I verify the installer is authentic?",
    a: "Every release ships with a SHA-256 fingerprint shown on the download card — click it to copy. After downloading, run 'certutil -hashfile Cyrion-Wallet-Setup.exe SHA256' in Windows and compare. If the hashes match, the file is byte-identical to what we published.",
  },
  {
    q: "What are the system requirements?",
    a: "Windows 10 or 11 (64-bit), 4 GB of RAM, and roughly 300 MB of disk space. No admin rights are needed for daily use — only for the initial installation.",
  },
  {
    q: "What happens if I lose my computer?",
    a: "During setup Cyrion gives you a recovery phrase. Write it down and store it offline — never in a photo, cloud note, or email. That phrase rebuilds your entire wallet on any new machine in minutes.",
  },
  {
    q: "Does Cyrion collect analytics or track me?",
    a: "No. Zero-Trace Privacy is a doctrine, not a toggle: the app contains no analytics SDKs, no telemetry beacons, and no crash-reporting pipelines that phone home. The only network calls are the ones you initiate.",
  },
  {
    q: "Is Cyrion free?",
    a: "The wallet is free to download and use. Atomic swaps and transfers only cost the native network fees of the chain you're using — Cyrion takes no cut.",
  },
  {
    q: "When are macOS, Linux, and mobile coming?",
    a: "They're Phase 03 on the roadmap and already in the forge. One vault, synced through your hardware enclave — never through our servers. Windows holders get migrated seamlessly.",
  },
];

export const Faq = () => {
  return (
    <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8 border-t border-white/5 relative overflow-hidden" data-testid="faq-section">
      <div className="absolute -top-24 right-1/4 w-[500px] h-[300px] rounded-full bg-mint/[0.04] blur-[120px] pointer-events-none" />
      <div className="relative max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-14"
        >
          <div className="inline-flex w-12 h-12 rounded-xl bg-gold/10 border border-gold/25 items-center justify-center mb-5">
            <MessageCircleQuestion className="w-6 h-6 text-gold" />
          </div>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-mint mb-4">Questions, answered</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100" data-testid="faq-heading">
            Trust is earned <span className="gold-text">in the details.</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        >
          <Accordion type="single" collapsible className="glass-panel rounded-3xl px-6 sm:px-10 py-2">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/[0.07] last:border-b-0" data-testid={`faq-item-${i + 1}`}>
                <AccordionTrigger
                  data-testid={`faq-trigger-${i + 1}`}
                  className="font-display text-base sm:text-lg font-semibold text-slate-200 hover:text-gold-soft hover:no-underline py-6 transition-colors duration-300 [&[data-state=open]>svg]:text-gold"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-slate-400 leading-relaxed pb-6" data-testid={`faq-content-${i + 1}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
